import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Check, X, Shield, ScanFace } from 'lucide-react';
import { isWebAuthnSupported, BIOMETRIC_EMAIL_KEY } from '@/lib/webauthn';

// Password strength checker
const checkPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    hasLetter: /[A-Za-z]/.test(password),
    hasNumber: /\d/.test(password),
  };
  const strength = Object.values(checks).filter(Boolean).length;
  return { checks, strength };
};

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [show2FAInput, setShow2FAInput] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  
  const { login, register, verify2FA, cancel2FA, loginWithBiometrics } = useAuth();
  const navigate = useNavigate();

  const biometricEmail = localStorage.getItem(BIOMETRIC_EMAIL_KEY);
  const biometricsAvailable = isWebAuthnSupported() && !!biometricEmail;

  // Where to send user after a successful login. Supports ?next=/path&review=1
  // Captured on mount — PublicRoute may strip the URL params before login completes.
  const redirectTargetRef = useRef('/dashboard');
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const next = params.get('next');
      const review = params.get('review');
      if (next) {
        const suffix = review ? (next.includes('?') ? `&review=${review}` : `?review=${review}`) : '';
        redirectTargetRef.current = `${next}${suffix}`;
      }
    } catch (e) { /* ignore */ }
  }, []);
  const getRedirectTarget = () => redirectTargetRef.current;

  const passwordStrength = checkPasswordStrength(formData.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLogin && !acceptedTerms) {
      toast.error('Du måste godkänna villkoren för att skapa ett konto');
      return;
    }

    // Check password strength for registration
    if (!isLogin && passwordStrength.strength < 3) {
      toast.error('Lösenordet uppfyller inte säkerhetskraven');
      return;
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result?.requires_2fa) {
          setShow2FAInput(true);
          toast.info('Verifieringskod skickad till din e-post');
        } else {
          toast.success('Välkommen tillbaka');
          navigate(getRedirectTarget());
        }
      } else {
        await register(formData.name, formData.email, formData.password);
        toast.success('Välkommen till Livon');
        navigate(getRedirectTarget());
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Något gick fel';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    setLoading(true);
    try {
      await loginWithBiometrics(biometricEmail);
      toast.success('Välkommen tillbaka');
      navigate(getRedirectTarget());
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        toast.error('Face ID avbröts');
      } else {
        toast.error(error?.message || 'Inloggning med Face ID misslyckades');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await verify2FA(twoFACode);
      toast.success('Välkommen tillbaka');
      navigate(getRedirectTarget());
    } catch (error) {
      const message = error.response?.data?.detail || 'Ogiltig verifieringskod';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    setShow2FAInput(false);
    setTwoFACode('');
    cancel2FA();
  };

  // 2FA verification screen
  if (show2FAInput) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading text-3xl font-semibold">Verifiering krävs</h1>
            <p className="text-muted-foreground">
              En 6-siffrig kod har skickats till din e-post. Ange koden nedan för att logga in.
            </p>
          </div>

          <form onSubmit={handle2FASubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="2fa-code">Verifieringskod</Label>
              <Input
                id="2fa-code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-14 text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
                autoFocus
                data-testid="2fa-code-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-full h-12 text-base btn-warm"
              disabled={loading || twoFACode.length !== 6}
              data-testid="verify-2fa-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verifiera'}
            </Button>
          </form>

          <div className="text-center">
            <button
              type="button"
              onClick={handleCancel2FA}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Avbryt och gå tillbaka
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left side - Logo with colorful background */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6a7a 30%, #3d8b8b 50%, #5a9a8b 70%, #7ab5a0 100%)'
        }}
      >
        {/* Subtle overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          background: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 70% 30%, rgba(255,255,255,0.15) 0%, transparent 50%)'
        }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          {/* Logo */}
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png" 
            alt="Livon" 
            className="h-44 w-auto mb-8 drop-shadow-2xl"
          />
          
          {/* Text */}
          <h2 className="font-heading text-4xl font-semibold text-white text-center drop-shadow-lg">
            En plats för hela dig
          </h2>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col">
        <div className="p-6 flex justify-between items-center gap-3">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tillbaka
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md space-y-8 animate-fade-in">
            <div className="text-center space-y-2">
              <h1 className="font-heading text-3xl md:text-4xl font-semibold">
                {isLogin ? 'Logga in' : 'Skapa konto'}
              </h1>
              <p className="text-muted-foreground">
                {isLogin ? 'Välkommen tillbaka' : 'Börja din utvecklingsresa'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">Namn</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                    placeholder="Ditt namn"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">E-post</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                  placeholder="din@email.se"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Lösenord</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="rounded-xl bg-secondary/50 border-0 focus-visible:ring-1 focus-visible:ring-primary h-12"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                {isLogin && (
                  <Link 
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline block text-right"
                  >
                    Glömt lösenord?
                  </Link>
                )}
                
                {/* Password strength indicator - only for registration */}
                {!isLogin && formData.password && (
                  <div className="space-y-2 pt-2">
                    <div className="flex gap-1">
                      {[1, 2, 3].map((level) => (
                        <div 
                          key={level}
                          className={`h-1 flex-1 rounded-full transition-colors ${
                            passwordStrength.strength >= level 
                              ? level === 1 ? 'bg-red-400' 
                              : level === 2 ? 'bg-yellow-400' 
                              : 'bg-green-500'
                              : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className={`flex items-center gap-2 ${passwordStrength.checks.length ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.checks.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Minst 8 tecken
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.checks.hasLetter ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.checks.hasLetter ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Innehåller bokstäver
                      </div>
                      <div className={`flex items-center gap-2 ${passwordStrength.checks.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {passwordStrength.checks.hasNumber ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                        Innehåller siffror
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Terms checkbox - only for registration */}
              {!isLogin && (
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="terms"
                      checked={acceptedTerms}
                      onCheckedChange={setAcceptedTerms}
                      className="mt-1"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                      Jag har läst och godkänner{' '}
                      <Link to="/privacy" className="text-primary hover:underline">
                        integritetspolicyn
                      </Link>{' '}
                      och{' '}
                      <Link to="/terms" className="text-primary hover:underline">
                        användarvillkoren
                      </Link>
                      . Jag förstår att min data sparas enligt GDPR.
                    </label>
                  </div>
                  
                  {/* Disclaimer notice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    <strong>OBS:</strong> Livon erbjuder inte medicinsk vård, psykoterapi eller diagnos. 
                    Vid psykisk ohälsa, kontakta legitimerad vårdgivare.
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full rounded-full h-12 text-base btn-warm transition-colors duration-300"
                disabled={loading || (!isLogin && !acceptedTerms)}
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  isLogin ? 'Logga in' : 'Skapa konto'
                )}
              </Button>
            </form>

            {/* Face ID / Touch ID login */}
            {isLogin && biometricsAvailable && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border/50" />
                  <span className="text-xs text-muted-foreground">eller</span>
                  <div className="flex-1 h-px bg-border/50" />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBiometricLogin}
                  disabled={loading}
                  className="w-full rounded-full h-12 text-base"
                  data-testid="biometric-login-btn"
                >
                  <ScanFace className="w-5 h-5 mr-2 text-primary" />
                  Logga in med Face ID / Touch ID
                </Button>
              </div>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setAcceptedTerms(false);
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
                data-testid="toggle-auth-btn"
              >
                {isLogin 
                  ? 'Har du inget konto? Skapa ett här' 
                  : 'Har du redan ett konto? Logga in'}
              </button>
            </div>

            {/* Footer links */}
            <div className="text-center pt-4 border-t border-border/50">
              <div className="flex justify-center gap-4 text-xs text-muted-foreground">
                <Link to="/privacy" className="hover:text-foreground">
                  Integritetspolicy
                </Link>
                <Link to="/terms" className="hover:text-foreground">
                  Användarvillkor
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
